import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, RotateCcw, ExternalLink } from 'lucide-react'

import { getFeed, revokeFeed } from '../lib/api'

export default function Settings() {
  const qc = useQueryClient()
  const [copied, setCopied] = useState('')
  const feed = useQuery({ queryKey: ['feed'], queryFn: getFeed })

  const revoke = useMutation({
    mutationFn: revokeFeed,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }),
  })

  const copy = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(''), 1200)
  }

  return (
    <div className="h-full overflow-auto">
      <header className="border-b border-line px-6 py-5">
        <div className="h-eyebrow">Preferences</div>
        <h1 className="h-section">Settings</h1>
      </header>

      <section className="p-6 max-w-3xl">
        <h2 className="text-base font-semibold text-ink-900 mb-1">Calendar subscription</h2>
        <p className="text-sm text-ink-500 mb-5 leading-relaxed">
          Add your PlusTag calendar to any standard calendar client (Google Calendar,
          iOS Calendar, macOS Calendar, Outlook) using one of the URLs below.
          Read-only in the subscriber — you edit events here in PlusTag Workspace.
        </p>

        {feed.isLoading && <div className="text-ink-500">Loading…</div>}
        {feed.data && (
          <div className="space-y-4">
            <UrlRow label="HTTPS URL (for Google Calendar &lsquo;From URL&rsquo;)"
                    url={feed.data.url}
                    onCopy={() => copy(feed.data.url, 'https')}
                    copied={copied === 'https'} />
            <UrlRow label="webcal:// URL (opens iOS / macOS Calendar subscribe dialog)"
                    url={feed.data.webcal_url}
                    onCopy={() => copy(feed.data.webcal_url, 'webcal')}
                    copied={copied === 'webcal'} />

            <div className="flex flex-wrap gap-2 pt-2">
              <a
                href={feed.data.google_add_url}
                target="_blank" rel="noreferrer"
                className="pill-primary"
              >
                <ExternalLink size={14} /> Add to Google Calendar
              </a>
              <a href={feed.data.webcal_url} className="pill-ghost">
                <ExternalLink size={14} /> Add to iOS / macOS Calendar
              </a>
              <button onClick={() => revoke.mutate()} className="pill-danger">
                <RotateCcw size={14} /> Revoke &amp; regenerate token
              </button>
            </div>

            <div className="text-xs text-ink-500 mt-3 max-w-lg leading-relaxed">
              Google refreshes subscribed calendars every ~24h (their limitation).
              iOS/macOS refresh every ~1h. If you need real-time two-way sync, hold
              on — CalDAV is on the roadmap for Phase E.
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

function UrlRow({ label, url, onCopy, copied }: {
  label: string; url: string; onCopy: () => void; copied: boolean;
}) {
  return (
    <div>
      <div className="h-eyebrow mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate text-xs card-inset px-3 py-2 font-mono text-ink-700">{url}</code>
        <button onClick={onCopy} className="pill-ghost">
          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}
